import React from "react";
import RegisterPage from "../components/RegisterPage";
import { useHSE } from "../lib/store";
import { ChartCard, ChartTooltip } from "../components/ChartCard";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Line, ComposedChart } from "recharts";

export default function Waste() {
  const { data, siteNames } = useHSE();
  const totalGen = data.waste.reduce((a, b) => a + b.generated, 0).toFixed(1);
  const totalRec = data.waste.reduce((a, b) => a + b.recycled, 0).toFixed(1);
  const recyclingPct = totalGen > 0 ? ((totalRec / totalGen) * 100).toFixed(1) : 0;

  const monthly = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m) => {
    const arr = data.waste.filter((w) => w.month === m);
    return {
      month: m,
      Generated: +arr.reduce((a, b) => a + b.generated, 0).toFixed(1),
      Recycled: +arr.reduce((a, b) => a + b.recycled, 0).toFixed(1),
    };
  });

  const Top = (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="grid grid-cols-3 gap-3 lg:col-span-1">
        <Stat label="Generated" value={`${totalGen} T`} tone="warning" />
        <Stat label="Recycled" value={`${totalRec} T`} tone="safe" />
        <Stat label="Rate" value={`${recyclingPct}%`} tone={recyclingPct >= 60 ? "safe" : "warning"} />
      </div>
      <div className="lg:col-span-2">
        <ChartCard title="Waste Generated vs Recycled" subtitle="Tonnes per month">
          <ResponsiveContainer width="100%" height={180}>
            <ComposedChart data={monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="month" tick={{ fill: "#455560", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#455560", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Generated" fill="#455560" radius={[4, 4, 0, 0]} />
              <Line dataKey="Recycled" stroke="#54A57B" strokeWidth={2.5} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );

  return (
    <RegisterPage
      title="Waste / Environment"
      subtitle="Waste generation, recycling and environmental events"
      storeKey="waste"
      idPrefix="WST"
      topPanels={Top}
      columns={[
        { key: "id", label: "ID" },
        { key: "month", label: "Month" },
        { key: "site", label: "Site" },
        { key: "generated", label: "Generated (T)" },
        { key: "recycled", label: "Recycled (T)" },
        { key: "recyclingPct", label: "Recycle %", render: (r) => <span className="tabular font-semibold text-[#54A57B]">{r.recyclingPct}%</span> },
        { key: "event", label: "Event" },
        { key: "status", label: "Status" },
      ]}
      formFields={[
        { key: "id", label: "Record ID" },
        { key: "month", label: "Month", type: "select", options: ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"] },
        { key: "site", label: "Site", type: "select", options: siteNames },
        { key: "generated", label: "Generated (Tonnes)", type: "number" },
        { key: "recycled", label: "Recycled (Tonnes)", type: "number" },
        { key: "recyclingPct", label: "Recycling %", type: "number" },
        { key: "event", label: "Environmental Event", type: "select", options: ["None", "Minor Spill", "Recycling Drive", "Audit"] },
        { key: "description", label: "Description", type: "textarea", full: true },
        { key: "status", label: "Status", type: "select", options: ["Recorded", "Verified"] },
      ]}
    />
  );
}

function Stat({ label, value, tone = "neutral" }) {
  const c = { safe: "#54A57B", warning: "#FFB517", critical: "#DC2626", neutral: "#183C55" }[tone];
  return (
    <div className="command-card p-3.5 border-l-4" style={{ borderLeftColor: c }}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="font-heading font-bold text-xl tabular text-[#0E2336] mt-0.5">{value}</div>
    </div>
  );
}
